import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BLOG_POSTS } from "../../data/homeData";
import SmartImg from "../ui/SmartImg";

const API = `${(typeof window !== "undefined" && window.location && window.location.origin ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}/api/content`;

const isInternal = (href = "") => href.startsWith("/");

// The three newest posts come from /api/content/blog (same source as the
// /blog listing page) so the homepage stays current automatically. The
// hardcoded BLOG_POSTS list is kept ONLY as a fallback so the section never
// renders blank if the API is unreachable.
const BlogPosts = () => {
  const [posts, setPosts] = useState(BLOG_POSTS);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/blog?page=1&page_size=3`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const items = (d.items || [])
          .filter((p) => p.title && p.slug)
          .map((p) => ({
            title: p.title,
            img: p.featured_image || "",
            href: `/blog/${p.slug}`,
          }));
        if (!cancelled && items.length) setPosts(items);
      })
      .catch(() => {}); // keep fallback
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="blog" data-testid="blog-section" className="py-20 bg-[var(--ac-cream)]">
      <div className="ac-container">
        <div className="text-center mb-12">
          <div className="eyebrow">From the Blog</div>
          <h2 className="section-heading mt-3" data-testid="blog-heading">
            Our Most Recent Blog Posts
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-7">
          {posts.map((p, i) => {
            const Wrap = isInternal(p.href) ? Link : "a";
            const wrapProps = isInternal(p.href)
              ? { to: p.href }
              : { href: p.href, target: "_blank", rel: "noopener noreferrer" };
            return (
              <Wrap
                key={p.title}
                {...wrapProps}
                className="lift bg-white border border-[var(--ac-line)] rounded-xl overflow-hidden flex flex-col"
                data-testid={`blog-card-${i}`}
              >
                <div className="aspect-[16/10] img-fallback relative overflow-hidden">
                  <SmartImg
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-[18px] font-bold text-[var(--ac-ink)] leading-snug">
                    {p.title}
                  </h3>
                  <div className="mt-5 inline-flex items-center gap-2 text-[var(--ac-red)] font-semibold text-sm">
                    Read More <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Wrap>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/blog"
            className="ds-btn ds-btn--secondary"
            data-testid="blog-show-more"
          >
            Show More
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPosts;
